import React, { useState, useRef, useEffect, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { chatbotApi, Conversation, ChatMessage as ChatMessageType } from '../../../services/api'
import {
    ChatbotContainer,
    ChatbotToggle,
    ChatWindow,
    ChatMainArea,
    ChatHeader,
    ChatTitle,
    HeaderButton,
    MessagesContainer,
    MessageBubble,
    MessageActions,
    MessageActionButton,
    TypingIndicator,
    EmptyState,
    InputContainer,
    MessageInput,
    SendButton,
    EditModeContainer,
    EditModeText,
    CancelEditButton,
    ConversationsSidebar,
    SidebarHeader,
    SidebarTitle,
    ConversationsList,
    ConversationItem,
    ConversationInfo,
    ConversationTitle,
    ConversationMeta,
    DeleteButton,
    NewConversationButton,
    MinimizedBar,
    MinimizedTitle,
    HeaderButtons,
} from './styles'

// Icons as simple SVG components
const ChatIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
    </svg>
)

const CloseIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
    </svg>
)

const MinimizeIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 13H5v-2h14v2z" />
    </svg>
)

const SendIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
    </svg>
)

const AddIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
    </svg>
)

const DeleteIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
    </svg>
)

const EditIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
    </svg>
)

const BotIcon = () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73A2 2 0 0 1 12 2M7.5 13A2.5 2.5 0 0 0 5 15.5 2.5 2.5 0 0 0 7.5 18a2.5 2.5 0 0 0 2.5-2.5A2.5 2.5 0 0 0 7.5 13m9 0a2.5 2.5 0 0 0-2.5 2.5 2.5 2.5 0 0 0 2.5 2.5 2.5 2.5 0 0 0 2.5-2.5 2.5 2.5 0 0 0-2.5-2.5z" />
    </svg>
)

interface Message {
    id: number
    role: 'user' | 'assistant'
    content: string
    created_at: string
}

export const ChatbotWidget: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false)
    const [isMinimized, setIsMinimized] = useState(false)
    const [conversations, setConversations] = useState<Conversation[]>([])
    const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null)
    const [messages, setMessages] = useState<Message[]>([])
    const [inputValue, setInputValue] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [isTyping, setIsTyping] = useState(false)
    const [editMode, setEditMode] = useState<number | null>(null)
    const [streamingContent, setStreamingContent] = useState('')

    const messagesEndRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLTextAreaElement>(null)

    // Load conversations on mount
    useEffect(() => {
        if (isOpen) {
            loadConversations()
        }
    }, [isOpen])

    // Scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [messages, streamingContent])

    // Auto-resize textarea
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.style.height = 'auto'
            inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`
        }
    }, [inputValue])

    const loadConversations = async () => {
        try {
            const response = await chatbotApi.getConversations()
            setConversations(response.data)
        } catch (error) {
            console.error('Error loading conversations:', error)
        }
    }

    const loadMessages = async (conversationId: string) => {
        try {
            const response = await chatbotApi.getMessages(conversationId)
            setMessages(response.data)
        } catch (error) {
            console.error('Error loading messages:', error)
        }
    }

    const handleCreateConversation = async () => {
        try {
            const response = await chatbotApi.createConversation()
            const newConversation = response.data
            setConversations(prev => [newConversation, ...prev])
            setCurrentConversation(newConversation)
            setMessages([])
        } catch (error) {
            console.error('Error creating conversation:', error)
        }
    }

    const handleSelectConversation = async (conversation: Conversation) => {
        setCurrentConversation(conversation)
        await loadMessages(conversation.id)
    }

    const handleDeleteConversation = async (e: React.MouseEvent, conversationId: string) => {
        e.stopPropagation()
        try {
            await chatbotApi.deleteConversation(conversationId)
            setConversations(prev => prev.filter(c => c.id !== conversationId))
            if (currentConversation?.id === conversationId) {
                setCurrentConversation(null)
                setMessages([])
            }
        } catch (error) {
            console.error('Error deleting conversation:', error)
        }
    }

    const handleSendMessage = async () => {
        if (!inputValue.trim() || isLoading) return

        // Create conversation if none exists
        let conversationId = currentConversation?.id
        if (!conversationId) {
            try {
                const response = await chatbotApi.createConversation()
                const newConversation = response.data
                setConversations(prev => [newConversation, ...prev])
                setCurrentConversation(newConversation)
                conversationId = newConversation.id
            } catch (error) {
                console.error('Error creating conversation:', error)
                return
            }
        }

        const messageContent = inputValue.trim()
        setInputValue('')
        setIsLoading(true)
        setIsTyping(true)
        setStreamingContent('')

        // Add user message immediately
        const userMessage: Message = {
            id: Date.now(),
            role: 'user',
            content: messageContent,
            created_at: new Date().toISOString(),
        }
        setMessages(prev => [...prev, userMessage])

        // Handle edit mode
        if (editMode !== null) {
            setEditMode(null)
            // Remove messages from the edited message onwards
            setMessages(prev => prev.filter(m => m.id < editMode))
            setMessages(prev => [...prev, userMessage])

            await chatbotApi.editLastMessage(
                conversationId,
                messageContent,
                (chunk) => {
                    setStreamingContent(prev => prev + chunk)
                },
                () => {
                    setIsTyping(false)
                    setIsLoading(false)
                    // Add the complete assistant message
                    setMessages(prev => [
                        ...prev,
                        {
                            id: Date.now(),
                            role: 'assistant',
                            content: streamingContent,
                            created_at: new Date().toISOString(),
                        },
                    ])
                    setStreamingContent('')
                    loadConversations()
                },
                (error) => {
                    console.error('Error:', error)
                    setIsTyping(false)
                    setIsLoading(false)
                }
            )
        } else {
            // Normal send
            let fullResponse = ''
            await chatbotApi.sendMessage(
                conversationId,
                messageContent,
                (chunk) => {
                    fullResponse += chunk
                    setStreamingContent(fullResponse)
                },
                () => {
                    setIsTyping(false)
                    setIsLoading(false)
                    // Add the complete assistant message
                    setMessages(prev => [
                        ...prev,
                        {
                            id: Date.now(),
                            role: 'assistant',
                            content: fullResponse,
                            created_at: new Date().toISOString(),
                        },
                    ])
                    setStreamingContent('')
                    loadConversations()
                },
                (error) => {
                    console.error('Error:', error)
                    setIsTyping(false)
                    setIsLoading(false)
                    setMessages(prev => [
                        ...prev,
                        {
                            id: Date.now(),
                            role: 'assistant',
                            content: `Error: ${error}`,
                            created_at: new Date().toISOString(),
                        },
                    ])
                }
            )
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSendMessage()
        }
    }

    const handleEditMessage = (message: Message) => {
        setEditMode(message.id)
        setInputValue(message.content)
        inputRef.current?.focus()
    }

    const cancelEdit = () => {
        setEditMode(null)
        setInputValue('')
    }

    const formatTime = (dateString: string) => {
        const date = new Date(dateString)
        return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
    }

    const formatDate = (dateString: string) => {
        const date = new Date(dateString)
        const now = new Date()
        const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))

        if (diffDays === 0) return 'Hoy'
        if (diffDays === 1) return 'Ayer'
        return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })
    }

    // Find the last user message for edit button visibility
    const lastUserMessageId = [...messages].reverse().find(m => m.role === 'user')?.id

    const handleMinimize = () => {
        setIsMinimized(true)
    }

    const handleRestore = () => {
        setIsMinimized(false)
    }

    return (
        <ChatbotContainer>
            {/* Minimized Bar */}
            {isOpen && isMinimized && (
                <MinimizedBar onClick={handleRestore}>
                    <MinimizedTitle>
                        {currentConversation?.title || 'Asistente IA'}
                        {isTyping && ' (escribiendo...)'}
                    </MinimizedTitle>
                    <HeaderButton onClick={(e) => { e.stopPropagation(); setIsOpen(false); setIsMinimized(false); }}>
                        <CloseIcon />
                    </HeaderButton>
                </MinimizedBar>
            )}

            {/* Full Chat Window */}
            <ChatWindow $isOpen={isOpen && !isMinimized}>
                {/* Conversations Sidebar - Always visible on the left */}
                <ConversationsSidebar>
                    <SidebarHeader>
                        <SidebarTitle>Conversaciones</SidebarTitle>
                    </SidebarHeader>
                    <NewConversationButton onClick={handleCreateConversation}>
                        <AddIcon /> Nueva conversación
                    </NewConversationButton>
                    <ConversationsList>
                        {conversations.length === 0 ? (
                            <EmptyState>
                                <p>No hay conversaciones</p>
                            </EmptyState>
                        ) : (
                            conversations.map(conv => (
                                <ConversationItem
                                    key={conv.id}
                                    $active={currentConversation?.id === conv.id}
                                    onClick={() => handleSelectConversation(conv)}
                                >
                                    <ConversationInfo>
                                        <ConversationTitle>{conv.title}</ConversationTitle>
                                        <ConversationMeta>
                                            {formatDate(conv.updated_at)} · {conv.message_count} mensajes
                                        </ConversationMeta>
                                    </ConversationInfo>
                                    <DeleteButton onClick={(e) => handleDeleteConversation(e, conv.id)}>
                                        <DeleteIcon />
                                    </DeleteButton>
                                </ConversationItem>
                            ))
                        )}
                    </ConversationsList>
                </ConversationsSidebar>

                {/* Main Chat Area */}
                <ChatMainArea>
                    <ChatHeader>
                        <ChatTitle>
                            {currentConversation?.title || 'Asistente IA'}
                        </ChatTitle>
                        <HeaderButtons>
                            <HeaderButton onClick={handleMinimize} title="Minimizar">
                                <MinimizeIcon />
                            </HeaderButton>
                            <HeaderButton onClick={() => setIsOpen(false)} title="Cerrar">
                                <CloseIcon />
                            </HeaderButton>
                        </HeaderButtons>
                    </ChatHeader>

                    <MessagesContainer>
                        {messages.length === 0 && !isTyping ? (
                            <EmptyState>
                                <BotIcon />
                                <p>¡Hola! Soy tu asistente. ¿En qué puedo ayudarte?</p>
                            </EmptyState>
                        ) : (
                            <>
                                {messages.map(message => (
                                    <div key={message.id}>
                                        <MessageBubble $role={message.role}>
                                            <ReactMarkdown
                                                remarkPlugins={[remarkGfm]}
                                                components={{
                                                    a: ({ href, children }) => (
                                                        <a href={href} target="_blank" rel="noopener noreferrer">
                                                            {children}
                                                        </a>
                                                    ),
                                                }}
                                            >{message.content}</ReactMarkdown>
                                        </MessageBubble>
                                        {message.role === 'user' && message.id === lastUserMessageId && !isLoading && (
                                            <MessageActions>
                                                <MessageActionButton onClick={() => handleEditMessage(message)}>
                                                    <EditIcon /> Editar
                                                </MessageActionButton>
                                            </MessageActions>
                                        )}
                                    </div>
                                ))}
                                {isTyping && streamingContent && (
                                    <MessageBubble $role="assistant">
                                        <ReactMarkdown
                                            remarkPlugins={[remarkGfm]}
                                            components={{
                                                a: ({ href, children }) => (
                                                    <a href={href} target="_blank" rel="noopener noreferrer">
                                                        {children}
                                                    </a>
                                                ),
                                            }}
                                        >{streamingContent}</ReactMarkdown>
                                    </MessageBubble>
                                )}
                                {isTyping && !streamingContent && (
                                    <TypingIndicator>
                                        <span />
                                        <span />
                                        <span />
                                    </TypingIndicator>
                                )}
                            </>
                        )}
                        <div ref={messagesEndRef} />
                    </MessagesContainer>

                    <InputContainer>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                            {editMode !== null && (
                                <EditModeContainer>
                                    <EditModeText>✏️ Editando mensaje</EditModeText>
                                    <CancelEditButton onClick={cancelEdit}>
                                        Cancelar
                                    </CancelEditButton>
                                </EditModeContainer>
                            )}
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <MessageInput
                                    ref={inputRef}
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Escribe un mensaje..."
                                    disabled={isLoading}
                                    rows={1}
                                />
                                <SendButton
                                    onClick={handleSendMessage}
                                    disabled={!inputValue.trim() || isLoading}
                                    $loading={isLoading}
                                >
                                    <SendIcon />
                                </SendButton>
                            </div>
                        </div>
                    </InputContainer>
                </ChatMainArea>
            </ChatWindow>

            <ChatbotToggle $isOpen={isOpen && !isMinimized} onClick={() => { setIsOpen(!isOpen); setIsMinimized(false); }}>
                {isOpen && !isMinimized ? <CloseIcon /> : <ChatIcon />}
            </ChatbotToggle>
        </ChatbotContainer>
    )
}

export default ChatbotWidget
